import React, { useState } from "react";
import { Table, Tag, Badge, Tooltip } from "antd";
import { Helmet } from "react-helmet-async";
import { FiBell, FiAlertTriangle, FiCheckCircle, FiInfo, FiClock, FiFilter, FiChevronRight } from "react-icons/fi";

export default function AlertsPage() {
    const [activeTab, setActiveTab] = useState("all");
    const [selectedSeverity, setSelectedSeverity] = useState(null);

    // Summary Statistics
    const alertStats = {
        totalAlerts: 1248,
        criticalAlerts: 23,
        warningAlerts: 156,
        infoAlerts: 892,
        unresolvedAlerts: 87,
        avgResolutionTime: "4.2h",
    };

    // Alert Data
    const allAlerts = [
        {
            id: "ALERT-001",
            title: "Critical Equipment Failure",
            severity: "Critical",
            category: "Equipment",
            source: "COMPRESSOR-01",
            timestamp: "2025-01-02 15:45:23",
            description: "Main air compressor pressure valve malfunction detected",
            status: "Unresolved",
            affectedSystems: ["COMPRESSOR-01", "AIR-SYSTEM-01"],
            resolution: "Pending maintenance",
            assignedTo: "John Smith",
        },
        {
            id: "ALERT-002",
            title: "Unexpected Network Disconnection",
            severity: "Critical",
            category: "Network",
            source: "INDUSTRIAL-SWITCH-01",
            timestamp: "2025-01-02 15:32:10",
            description: "Network interface eth0 disconnected from INDUSTRIAL-SWITCH-01",
            status: "Unresolved",
            affectedSystems: ["SCADA-01", "HMI-01"],
            resolution: "Network team investigating",
            assignedTo: "Sarah Johnson",
        },
        {
            id: "ALERT-003",
            title: "Database Connection Timeout",
            severity: "High",
            category: "Database",
            source: "HISTORIAN-01",
            timestamp: "2025-01-02 15:15:48",
            description: "Historical database connection exceeded timeout threshold",
            status: "Unresolved",
            affectedSystems: ["HISTORIAN-01"],
            resolution: "Database restart scheduled",
            assignedTo: "Mike Chen",
        },
        {
            id: "ALERT-004",
            title: "Unusual Process Behavior",
            severity: "High",
            category: "Process",
            source: "PLC-02",
            timestamp: "2025-01-02 14:58:15",
            description: "PLC-02 executing non-standard control sequence",
            status: "Investigating",
            affectedSystems: ["PLC-02", "SENSOR-NETWORK-02"],
            resolution: "Under investigation by control team",
            assignedTo: "Alex Williams",
        },
        {
            id: "ALERT-005",
            title: "Storage Capacity Warning",
            severity: "Medium",
            category: "Storage",
            source: "LOG-SERVER",
            timestamp: "2025-01-02 14:42:31",
            description: "Log server storage capacity at 87% utilization",
            status: "Acknowledged",
            affectedSystems: ["LOG-SERVER"],
            resolution: "Log rotation scheduled for tonight",
            assignedTo: "Emma Davis",
        },
        {
            id: "ALERT-006",
            title: "Configuration Drift Detected",
            severity: "Medium",
            category: "Configuration",
            source: "MGMT-SERVER-01",
            timestamp: "2025-01-02 14:25:09",
            description: "MGMT-SERVER-01 configuration differs from baseline",
            status: "Investigating",
            affectedSystems: ["MGMT-SERVER-01"],
            resolution: "Configuration audit in progress",
            assignedTo: "John Smith",
        },
        {
            id: "ALERT-007",
            title: "Backup Job Failed",
            severity: "Medium",
            category: "Backup",
            source: "BACKUP-SERVER",
            timestamp: "2025-01-02 14:08:44",
            description: "Daily backup job failed to complete successfully",
            status: "Resolved",
            affectedSystems: ["BACKUP-SERVER"],
            resolution: "Backup restarted and completed successfully",
            assignedTo: "Sarah Johnson",
        },
        {
            id: "ALERT-008",
            title: "High CPU Usage Detected",
            severity: "Medium",
            category: "Performance",
            source: "HMI-01",
            timestamp: "2025-01-02 13:51:22",
            description: "HMI-01 CPU usage sustained above 85%",
            status: "Acknowledged",
            affectedSystems: ["HMI-01"],
            resolution: "Processes being analyzed for optimization",
            assignedTo: "Mike Chen",
        },
        {
            id: "ALERT-009",
            title: "Routine Maintenance Reminder",
            severity: "Low",
            category: "Maintenance",
            source: "SENSOR-NETWORK-01",
            timestamp: "2025-01-02 13:30:15",
            description: "Scheduled maintenance window for SENSOR-NETWORK-01",
            status: "Scheduled",
            affectedSystems: ["SENSOR-NETWORK-01"],
            resolution: "Maintenance scheduled for 2025-01-05",
            assignedTo: "Alex Williams",
        },
        {
            id: "ALERT-010",
            title: "License Expiration Warning",
            severity: "Low",
            category: "License",
            source: "LICENSE-SERVER",
            timestamp: "2025-01-02 13:12:48",
            description: "Software license expiring in 30 days",
            status: "Acknowledged",
            affectedSystems: ["LICENSE-SERVER"],
            resolution: "License renewal order placed",
            assignedTo: "Emma Davis",
        },
    ];

    // Helper functions
    const getSeverityColor = (severity) => {
        const colors = {
            Critical: "#ef4444",
            High: "#f97316",
            Medium: "#eab308",
            Low: "#84cc16",
        };
        return colors[severity] || "#6b7280";
    };

    const getSeverityBg = (severity) => {
        const backgrounds = {
            Critical: "#fee2e2",
            High: "#fed7aa",
            Medium: "#fef3c7",
            Low: "#dcfce7",
        };
        return backgrounds[severity] || "#f3f4f6";
    };

    const getStatusColor = (status) => {
        const colors = {
            Unresolved: "#ef4444",
            Investigating: "#f59e0b",
            Acknowledged: "#3b82f6",
            Scheduled: "#8b5cf6",
            Resolved: "#10b981",
        };
        return colors[status] || "#6b7280";
    };

    const filteredAlerts = allAlerts.filter((alert) => {
        if (activeTab === "all") return true;
        if (activeTab === "critical") return alert.severity === "Critical";
        if (activeTab === "warning") return ["High", "Medium"].includes(alert.severity);
        if (activeTab === "info") return alert.severity === "Low";
        if (activeTab === "unresolved") return ["Unresolved", "Investigating"].includes(alert.status);
        return true;
    });

    // Table columns
    const alertColumns = [
        {
            title: "Alert ID",
            dataIndex: "id",
            key: "id",
            render: (text) => <span className="fw-bold text-gray-900 fs-6">{text}</span>,
            width: 110,
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            render: (text) => <span className="fw-semibold text-gray-900">{text}</span>,
        },
        {
            title: "Severity",
            dataIndex: "severity",
            key: "severity",
            render: (severity) => (
                <Tag color={getSeverityColor(severity)} style={{ borderRadius: "4px", fontWeight: "600" }}>
                    {severity}
                </Tag>
            ),
            width: 100,
        },
        {
            title: "Source",
            dataIndex: "source",
            key: "source",
            render: (source) => <span className="text-gray-700 fs-6">{source}</span>,
            width: 120,
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category",
            render: (category) => <Tag style={{ borderRadius: "4px", background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6" }}>{category}</Tag>,
            width: 110,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={getStatusColor(status)} style={{ borderRadius: "4px", fontWeight: "600" }}>
                    {status}
                </Tag>
            ),
            width: 110,
        },
        {
            title: "Timestamp",
            dataIndex: "timestamp",
            key: "timestamp",
            width: 160,
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <button type="button" className="btn btn-sm btn-primary fw-bold">
                    View
                </button>
            ),
            width: 80,
        },
    ];

    return (



        <>
            <Helmet>
                <title>Network | Sec1 Software Pvt. Ltd.</title>
            </Helmet>

            <div id="kt_app_toolbar" className="app-toolbar pt-12 rounded-0">
                <div
                    id="kt_app_toolbar_container"
                    className="app-container container-fluid d-flex align-items-stretch"
                >
                    <div className="app-toolbar-wrapper d-flex flex-stack flex-wrap gap-4 w-100">
                        <div className="page-title d-flex flex-column gap-1 me-3 mb-2">
                            <h1 className="page-heading d-flex flex-column justify-content-center text-dark fw-bolder fs-1 lh-0">
                                Alerts & Notifications
                            </h1>
                        </div>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div
                    id="kt_app_content_container"
                    className="app-container container-fluid"
                >
                    <div className="mb-3">


                        <div className="d-flex flex-column gap-4 mb-5">
                            {/* Page Header */}
                            {/* <div style={{ background: "linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(249, 115, 22, 0.04) 100%)", borderRadius: "12px", padding: "32px 28px", border: "1px solid rgba(239, 68, 68, 0.1)" }}>
                                <div className="d-flex align-items-center gap-3 mb-2">
                                    <div style={{ width: "56px", height: "56px", borderRadius: "10px", background: "linear-gradient(135deg, #ef4444 0%, #f97316 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(239, 68, 68, 0.3)" }}>
                                        <FiBell size={32} color="white" />
                                    </div>
                                    <div>
                                        <h1 className="fw-bold text-gray-900 mb-1 fs-3">System Alerts & Notifications</h1>
                                        <p className="text-gray-600 mb-0">Real-time monitoring of OT infrastructure alerts and system notifications</p>
                                    </div>
                                </div>
                            </div> */}

                            {/* Summary Cards */}
                            <div className="row g-2 mb-4">
                                {/* Total Alerts Card */}
                                <div className="col-lg-3 col-md-3 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #6b7280", borderRadius: "8px", boxShadow: "0 4px 20px rgba(107, 114, 128, 0.15)", background: "linear-gradient(135deg, rgba(107, 114, 128, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Total Alerts</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{alertStats.totalAlerts}</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(107, 114, 128, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiBell size={28} color="#6b7280" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Critical Alerts Card */}
                                <div className="col-lg-3 col-md-3 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #ef4444", borderRadius: "8px", boxShadow: "0 4px 20px rgba(239, 68, 68, 0.15)", background: "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Critical Alerts</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{alertStats.criticalAlerts}</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(239, 68, 68, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiAlertTriangle size={28} color="#ef4444" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Unresolved Alerts Card */}
                                <div className="col-lg-3 col-md-3 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #f59e0b", borderRadius: "8px", boxShadow: "0 4px 20px rgba(245, 158, 11, 0.15)", background: "linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Unresolved Alerts</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{alertStats.unresolvedAlerts}</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(245, 158, 11, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiCheckCircle size={28} color="#f59e0b" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Warning Alerts Card */}
                                <div className="col-lg-3 col-md-3 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #f97316", borderRadius: "8px", boxShadow: "0 4px 20px rgba(249, 115, 22, 0.15)", background: "linear-gradient(135deg, rgba(249, 115, 22, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Warning Alerts</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{alertStats.warningAlerts}</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(249, 115, 22, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiAlertTriangle size={28} color="#f97316" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Info Alerts Card */}
                                <div className="col-lg-3 col-md-3 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #3b82f6", borderRadius: "8px", boxShadow: "0 4px 20px rgba(59, 130, 246, 0.15)", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Info Alerts</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{alertStats.infoAlerts}</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(59, 130, 246, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiInfo size={28} color="#3b82f6" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Avg Resolution Time Card */}
                                <div className="col-lg-3 col-md-3 col-sm-12">
                                    <div className="card h-100" style={{ borderLeft: "4px solid #10b981", borderRadius: "8px", boxShadow: "0 4px 20px rgba(16, 185, 129, 0.15)", background: "linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(0, 0, 0, 0) 100%)" }}>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <p className="fw-semibold fs-6 text-gray-500 mb-2">Avg Resolution Time</p>
                                                    <h3 className="text-gray-900 fs-2 fw-bold mb-0">{alertStats.avgResolutionTime}</h3>
                                                </div>
                                                <div style={{ width: "50px", height: "50px", borderRadius: "8px", background: "rgba(16, 185, 129, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <FiClock size={28} color="#10b981" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Tab Navigation */}
                            <div className="d-flex gap-3 border-bottom mb-5 pb-2" style={{ background: "linear-gradient(to right, rgba(239, 68, 68, 0.02), rgba(0, 0, 0, 0))" }}>
                                <button
                                    type="button"
                                    className={`btn btn-outline-0 fw-bold pb-3 fs-6 position-relative ${activeTab === "all" ? "text-primary" : "text-gray-600"}`}
                                    onClick={() => setActiveTab("all")}
                                    style={{ borderRadius: 0, background: "transparent", border: "none", transition: "all 0.3s ease" }}
                                >
                                    All Alerts
                                    {activeTab === "all" && <div style={{ position: "absolute", bottom: "-12px", left: "0", right: "0", height: "3px", background: "linear-gradient(to right, #ef4444, #f97316)", borderRadius: "2px" }} />}
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-outline-0 fw-bold pb-3 fs-6 position-relative ${activeTab === "critical" ? "text-primary" : "text-gray-600"}`}
                                    onClick={() => setActiveTab("critical")}
                                    style={{ borderRadius: 0, background: "transparent", border: "none", transition: "all 0.3s ease" }}
                                >
                                    Critical
                                    {activeTab === "critical" && <div style={{ position: "absolute", bottom: "-12px", left: "0", right: "0", height: "3px", background: "linear-gradient(to right, #ef4444, #f97316)", borderRadius: "2px" }} />}
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-outline-0 fw-bold pb-3 fs-6 position-relative ${activeTab === "warning" ? "text-primary" : "text-gray-600"}`}
                                    onClick={() => setActiveTab("warning")}
                                    style={{ borderRadius: 0, background: "transparent", border: "none", transition: "all 0.3s ease" }}
                                >
                                    Warnings
                                    {activeTab === "warning" && <div style={{ position: "absolute", bottom: "-12px", left: "0", right: "0", height: "3px", background: "linear-gradient(to right, #ef4444, #f97316)", borderRadius: "2px" }} />}
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-outline-0 fw-bold pb-3 fs-6 position-relative ${activeTab === "info" ? "text-primary" : "text-gray-600"}`}
                                    onClick={() => setActiveTab("info")}
                                    style={{ borderRadius: 0, background: "transparent", border: "none", transition: "all 0.3s ease" }}
                                >
                                    Info
                                    {activeTab === "info" && <div style={{ position: "absolute", bottom: "-12px", left: "0", right: "0", height: "3px", background: "linear-gradient(to right, #ef4444, #f97316)", borderRadius: "2px" }} />}
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-outline-0 fw-bold pb-3 fs-6 position-relative ${activeTab === "unresolved" ? "text-primary" : "text-gray-600"}`}
                                    onClick={() => setActiveTab("unresolved")}
                                    style={{ borderRadius: 0, background: "transparent", border: "none", transition: "all 0.3s ease" }}
                                >
                                    Unresolved
                                    {activeTab === "unresolved" && <div style={{ position: "absolute", bottom: "-12px", left: "0", right: "0", height: "3px", background: "linear-gradient(to right, #ef4444, #f97316)", borderRadius: "2px" }} />}
                                </button>
                            </div>

                            {/* Critical Alerts Preview Cards */}
                            {allAlerts.filter(a => a.severity === "Critical").length > 0 && (
                                <div className="card mt-5">
                                    <div className="card-body">
                                        <h5 className="fw-bold text-gray-900 mb-4">Critical Alerts Requiring Immediate Action</h5>
                                        <div className="row g-3">
                                            {allAlerts.filter(a => a.severity === "Critical").map((alert) => (
                                                <div key={alert.id} className="col-12 col-lg-6">
                                                    <div className="p-4 border rounded" style={{ borderColor: "#e5e7eb", background: "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(0, 0, 0, 0) 100%)", borderLeft: "4px solid #ef4444", boxShadow: "0 4px 12px rgba(239, 68, 68, 0.12)" }}>
                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                                    <h6 className="fw-bold text-gray-900 mb-0">{alert.title}</h6>
                                                                    <Tag color="#ef4444" style={{ borderRadius: "4px", fontWeight: "600", fontSize: "11px" }}>CRITICAL</Tag>
                                                                </div>
                                                                <p className="text-gray-600 fs-6 mb-2">{alert.description}</p>
                                                                <div className="d-flex gap-2 flex-wrap">
                                                                    <Tag style={{ borderRadius: "4px", background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6" }}>{alert.source}</Tag>
                                                                    <Tag style={{ borderRadius: "4px", background: "rgba(249, 115, 22, 0.1)", color: "#f97316" }}>{alert.category}</Tag>
                                                                    <Tag color={getStatusColor(alert.status)} style={{ borderRadius: "4px", fontWeight: "600" }}>{alert.status}</Tag>
                                                                </div>
                                                            </div>
                                                            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "rgba(239, 68, 68, 0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                                <FiAlertTriangle size={20} color="#ef4444" />
                                                            </div>
                                                        </div>
                                                        <div className="mt-3 pt-3 border-top" style={{ borderColor: "rgba(0,0,0,0.1)" }}>
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <small className="text-gray-600"><strong>Assigned:</strong> {alert.assignedTo}</small>
                                                                <button type="button" className="btn btn-sm btn-primary fw-bold d-flex align-items-center gap-2">
                                                                    View Details
                                                                    <FiChevronRight size={14} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="card mt-5">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h5 className="fw-bold text-gray-900 mb-0">Alert Details</h5>
                                        <div className="d-flex gap-2">
                                            <button type="button" className="btn btn-sm btn-outline-secondary fw-bold d-flex align-items-center gap-2">
                                                <FiFilter size={16} />
                                                Filter
                                            </button>
                                            <button type="button" className="btn btn-sm btn-primary fw-bold">
                                                Export Alerts
                                            </button>
                                        </div>
                                    </div>
                                    <Table
                                        dataSource={filteredAlerts}
                                        columns={alertColumns}
                                        pagination={{ pageSize: 10, total: filteredAlerts.length, className: "mb-0" }}
                                        scroll={{ x: 1200 }}
                                        className="antd-override"
                                        rowKey={(record) => record.id}
                                    />
                                </div>
                            </div>
                        </div>


                    </div>

                </div>
            </div>
        </>










    );
}
